import { ActionPermissionsService } from './../../core/services/action-permissions.service';
import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import * as moment from 'moment';
import {TaskRequestSidenavService} from '../../core/services/task-request-sidenav.service';
import {MatDialog} from '@angular/material/dialog';
import {StockDetailsModalComponent} from '../../modals/stock-details-modal/stock-details-modal.component';
import {TenantConfig} from '../../core/resolvers/tenant-config-resolver.resolver';
import {StockFilterParams, StockService, StockSortParams} from '../../core/services/stock.service';
import { Subscription } from 'rxjs';
import { ErrorService } from '../../core/services/error.service';
import { CratesDataSourceService } from 'src/app/core/data-sources/crates-data-source.service';
import { UserRoles } from '../../../models/User';
import { ActionsEnum, SendTasksTypeEnum } from 'src/app/core/data-sources/action-data-source.service';
import { TenantInterface } from 'src/models/Tenant';
import { TenantService } from 'src/app/core/services/tenant.service';
import { Crate } from 'src/models/Crate';
@Component({
  selector: 'app-stock',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.scss']
})
export class StockComponent implements OnInit, OnDestroy {
  filterParams: StockFilterParams;
  actualHour;
  tenantConfig: TenantConfig;
  selectedCratesIds = new Set();
  pendingTaskHasError: boolean;
  isLoading = false;
  isModalLoading = false;
  items: Crate[] = [];
  itemsSubscription: Subscription;
  commentsSubscription$: Subscription;
  sortParams: StockSortParams;
  totalProductsCount = 0;
  userRole: UserRoles = null;
  sendTaskTypesEnum = SendTasksTypeEnum;
  tenant: TenantInterface;
  palletsUnderAlert: Crate[] = [];
  alertedCrateIds: Set<string> = new Set();

  constructor(private router: Router,
              private route: ActivatedRoute,
              public stockService: StockService,
              private sidenavService: TaskRequestSidenavService,
              public dialog: MatDialog,
              private errorService: ErrorService,
              private cratesService: CratesDataSourceService,
              private actionPermissionsService: ActionPermissionsService,
              private tenantService: TenantService
              ) {
  }

  ngOnDestroy(): void {
    this.itemsSubscription?.unsubscribe();
    this.commentsSubscription$?.unsubscribe();
  }

  ngOnInit(): void {
    const map = new Map<string, Crate>();
    this.actualHour = moment().format('HH:mm');
    this.isLoading = true;
    this.stockService.init(this.filterParams).catch(err => console.error);
    this.getProductsCount();
    this.itemsSubscription = this.stockService.items$.subscribe(async (items) => {
      map.clear();
      const data = [...items];
      for (const d of data) {
        map.set(d.crateId, d);
      }
      this.items = data;
      await this.getPalletsUnderAlert();
      this.isLoading = false;
    });

    this.commentsSubscription$ = this.stockService.crateCommentsQty$.subscribe(comments => {
      if (!comments || !comments.length) return;
      for (const comment of comments) {
        if (map.has(comment.crateId)) {
          map.get(comment.crateId).commentsQty = comment.commentsQty;
        }
      }
    });
    this.tenantConfig = this.route.snapshot.data.tenantConfig;
    this.userRole = this.route.snapshot.data.role;
    this.tenantService.get().then(t => this.tenant = t);
  }

  canClickButton(action: ActionsEnum | SendTasksTypeEnum, role: UserRoles) {
    if (!this.tenant?.features.relocation) {
      return false;
    }
    return this.actionPermissionsService.hasPermission(action, role);
  }

  canDownloadCsv() {
    return [UserRoles.ADMIN, UserRoles.MASTER].includes(this.userRole);
  }

  async openSidenav(type) {
    const data = {
      type,
      data: {
        isPicking: false
      }
    };
    this.sidenavService.toggle(data).then(async (res: any) => {
      if (res.isCritical) {
        await this.update();
      } else if (!res.isCritical && res.userIds) {
        await this.update();
      }
    });
  }

  async itemColumnClicked($event){
    const {crate, column} = $event;
    if (column === 'check'){
      if (!this.selectedCratesIds.has(crate.id)){
        this.selectedCratesIds.add(crate.id);
      } else{
        this.selectedCratesIds.delete(crate.id);
      }
    } else{
      this.openDetailsModal(crate, column);
    }
  }

  async openDetailsModal(crate, column){
    try {
      this.isModalLoading = true;
      const page = column === 'messages' ? 3 : 0;
      const itemDetails = await this.cratesService.getCrateById(crate.id).then((r: any) => {
        r.invoice = r.crate.invoices[0];
        return r;
      });
      const dialogRef = this.dialog.open(StockDetailsModalComponent, {
        panelClass: 'details-modal',
        maxWidth: '85vw',
        data: {item: crate, itemDetails, page},
        autoFocus: false
      });
      dialogRef.afterClosed().subscribe(res => {
        if (res === 'SUBMITTED') {
          this.update();
        } else if (res?.openPalletInventorySidenav) {
          const data = { type: "PALLET_INVENTORY", crate: res.crate, position: itemDetails.position };
          this.sidenavService.toggle(data);
        }
      });
    } catch (e) {
      this.errorService.openErrorSnackBar(e);
    } finally {
      this.isModalLoading = false;
    }
  }

  async getNextPage(event: boolean){
    this.selectedCratesIds.clear();
    await this.stockService.getNextPage(this.filterParams, this.sortParams);
  }
  async getPreviousPage(event: boolean){
    this.selectedCratesIds.clear();
    await this.stockService.getPreviousPage(this.filterParams, this.sortParams);
  }

  async onPageSizeChange(event: number){
    this.selectedCratesIds.clear();
    await this.stockService.setPageSize(event);
  }
  async onPageChange(event: number){
    this.selectedCratesIds.clear();
    await this.stockService.setPage(event, this.filterParams, this.sortParams);
  }

  async onFiltersApply(params: { sortParams: StockSortParams, filterParams: StockFilterParams }) {
    this.stockService.currentPage = 1;
    this.filterParams = params?.filterParams;
    this.sortParams = this.setSortParams(params?.sortParams);
    await this.update();
  }

  async update() {
    try {
      this.isLoading = true;
      await this.stockService.reload(this.filterParams, this.sortParams);
      this.getProductsCount();
      this.isLoading = false;
      this.actualHour = moment().format('HH:mm');
    } catch (e) {
      this.errorService.openErrorSnackBar(e);
      this.isLoading = false;
    }
  }

  async getCSVStorage(){
    try {
      const csv = await this.stockService.getCSVStorage(this.filterParams);

      const url = window.URL.createObjectURL(csv);

      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${moment().format("yyy-MM-DD-hh-mm-ss")}-quantum-storage.csv`;
      downloadLink.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      this.errorService.openErrorSnackBar(e);
    }
  }

  async toDashboard() {
    await this.router.navigate(['inspection']);
    this.itemsSubscription.unsubscribe();
  }

  async toExit() {
    await this.router.navigate(['exit']);
    this.itemsSubscription.unsubscribe();
  }

  visiblePendingTaskError() {
    this.pendingTaskHasError = true;
  }

  setSortParams(sort: StockSortParams) {
    if (!sort || !sort.sortBy) {
      return;
    }
    this.selectedCratesIds.clear();
    return sort;
  }

  getProductsCount() {
    this.cratesService.getProductsCount("STOCK", this.filterParams).then((res) => {
      this.totalProductsCount = res.allProducts.total;
    });
  }

  async getPalletsUnderAlert() {
    if (this.items.length >= 1) {
      const crateIds = this.items.map(crate => crate.id);
      try {
        const res = await this.cratesService.getPalletsUnderAlert(crateIds);
        this.palletsUnderAlert = res.filter(crate => crate.cratesUnderAlert && crate.cratesUnderAlert.length);
        this.alertedCrateIds.clear();
        this.palletsUnderAlert.forEach(p => this.alertedCrateIds.add(p.id));
        return this.palletsUnderAlert;
      } catch (e) {
        this.errorService.openErrorSnackBar(e);
        this.palletsUnderAlert = [];
        this.alertedCrateIds.clear();
      }
    }
  }

  isPalletUnderAlert(crateId: string) {
    return this.alertedCrateIds.has(crateId);
  }

}
